/**
 * O menu lateral do painel. Largo: a árvore com grupos que abrem e fecham (o grupo da página
 * actual abre sozinho; os que a pessoa abre ou fecha ficam guardados neste aparelho) e uma
 * pesquisa no topo que filtra sem ligar a acentos. Recolhido: só os ícones; numa folha, a
 * dica com o nome; num grupo, os sub-itens num menu flutuante à direita. Um sub-item com
 * `descricao` (para que serve: as entradas dos módulos dos pacotes) mostra-a numa dica à direita.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dica } from "../sobreposicoes";
import { filtrarMenu, ItemPainel, somaDasContagens } from "./menu";

/**
 * A cor do ícone de um item que a traz (`ItemPainel.cor`, um nome do Tailwind). Hoje nenhum a traz:
 * os ícones do menu são monocromáticos, também os dos pacotes (decisão do dono, 30/10). Por
 * extenso: o Tailwind só gera as classes que encontra escritas.
 */
const CORES_DO_ICONE: Record<string, string> = {
    blue: "text-blue-500 dark:text-blue-400",
    sky: "text-sky-500 dark:text-sky-400",
    cyan: "text-cyan-500 dark:text-cyan-400",
    teal: "text-teal-500 dark:text-teal-400",
    emerald: "text-emerald-500 dark:text-emerald-400",
    green: "text-green-500 dark:text-green-400",
    lime: "text-lime-500 dark:text-lime-400",
    yellow: "text-yellow-500 dark:text-yellow-400",
    amber: "text-amber-500 dark:text-amber-400",
    orange: "text-orange-500 dark:text-orange-400",
    red: "text-red-500 dark:text-red-400",
    rose: "text-rose-500 dark:text-rose-400",
    pink: "text-pink-500 dark:text-pink-400",
    fuchsia: "text-fuchsia-500 dark:text-fuchsia-400",
    purple: "text-purple-500 dark:text-purple-400",
    violet: "text-violet-500 dark:text-violet-400",
    indigo: "text-indigo-500 dark:text-indigo-400",
    slate: "text-slate-500 dark:text-slate-400",
    stone: "text-stone-500 dark:text-stone-400",
};

function lerGrupos(chave: string): Record<string, boolean> {
    try {
        return JSON.parse(localStorage.getItem(chave) ?? "{}") ?? {};
    } catch {
        return {};
    }
}

function Contagem({ valor, aceso }: { valor?: number | string | null; aceso?: boolean }) {
    if (valor === null || valor === undefined || valor === "" || valor === 0 || valor === "0") {
        return null;
    }
    return (
        <span
            className={cn(
                "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
                aceso ? "bg-portal text-white dark:bg-portal-claro dark:text-zinc-950" : "bg-red-500 text-white dark:bg-red-500/90",
            )}
        >
            {valor}
        </span>
    );
}

/** Um link do menu: do Inertia, ou `<a>` se for externo ou doutro domínio. */
function LinkMenu({ item, className, children, onNavegar, ...resto }: { item: ItemPainel; className?: string; children: React.ReactNode; onNavegar?: () => void } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
    const absoluto = !!item.href && /^https?:\/\//.test(item.href) && typeof window !== "undefined" && !item.href.startsWith(window.location.origin);
    if (item.externo || absoluto) {
        return (
            <a href={item.href} target={item.externo ? "_blank" : undefined} rel={item.externo ? "noreferrer" : undefined} className={className} onClick={onNavegar} {...resto}>
                {children}
            </a>
        );
    }
    return (
        <Link href={item.href ?? "#"} className={className} onClick={onNavegar} {...(resto as Record<string, unknown>)}>
            {children}
        </Link>
    );
}

export function MenuLateral({ itens, activos, recolhido = false, chave, onNavegar, pesquisavel = true, nome = "Menu" }: {
    itens: ItemPainel[];
    /** O caminho aceso (`caminhoActivo`). */
    activos: ItemPainel[];
    recolhido?: boolean;
    /** Prefixo do `localStorage` (os grupos abertos). */
    chave: string;
    /** Depois de tocar num link (fecha a gaveta do telemóvel). */
    onNavegar?: () => void;
    pesquisavel?: boolean;
    nome?: string;
}) {
    const chaveGrupos = `${chave}-grupos`;
    const [grupos, setGrupos] = useState<Record<string, boolean>>(() => lerGrupos(chaveGrupos));
    const [termo, setTermo] = useState("");
    const chavesActivas = useMemo(() => new Set(activos.map((a) => a.chave)), [activos]);
    const folhaActiva = activos[activos.length - 1]?.chave;
    const aPesquisar = termo.trim().length > 0;
    const visiveis = useMemo(() => filtrarMenu(itens, termo), [itens, termo]);
    const navRef = useRef<HTMLElement>(null);

    // O item aceso fica à vista ao abrir a página (num menu comprido podia estar lá em baixo).
    useEffect(() => {
        const aceso = navRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
        aceso?.scrollIntoView({ block: "nearest" });
    }, [folhaActiva, recolhido]);

    const alternarGrupo = (item: ItemPainel, abertoAgora: boolean) => {
        setGrupos((g) => {
            const novo = { ...g, [item.chave]: !abertoAgora };
            try {
                localStorage.setItem(chaveGrupos, JSON.stringify(novo));
            } catch {
                // Sem localStorage os grupos seguem só a página.
            }
            return novo;
        });
    };

    const grupoAberto = (item: ItemPainel) => aPesquisar || (grupos[item.chave] ?? chavesActivas.has(item.chave));

    /**
     * Mexer no seletor de um grupo (a escola, o hotel, o hospital…) deixa o grupo aberto: o
     * seletor troca os sub-itens pelos do sítio escolhido, nenhum deles é a página aberta, e o
     * grupo — que estava aberto só por estar «no caminho» — fechava-se debaixo do rato.
     */
    const fixarAberto = (item: ItemPainel) => setGrupos((g) => (g[item.chave] ? g : { ...g, [item.chave]: true }));

    /** Os sub-itens de um grupo (fundura ≥ 1), com linha à esquerda. */
    const subArvore = (lista: ItemPainel[], fundura: number, aoNavegar?: () => void): React.ReactNode => (
        <ul className={cn("space-y-0.5", fundura === 1 ? "ml-[21px] mt-1 border-l border-zinc-200 pl-3 dark:border-white/10" : "ml-2 mt-0.5 border-l border-zinc-200 pl-2.5 dark:border-white/10")}>
            {lista.map((sub) => {
                if (sub.itens?.length) {
                    const aberto = grupoAberto(sub);
                    const noCaminho = chavesActivas.has(sub.chave);
                    return (
                        <li key={sub.chave}>
                            <button
                                type="button"
                                aria-expanded={aberto}
                                onClick={() => alternarGrupo(sub, aberto)}
                                className={cn(
                                    "flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors md:min-h-0",
                                    noCaminho ? "font-semibold text-zinc-900 dark:text-white" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white",
                                )}
                            >
                                <span className="flex-1 truncate">{sub.rotulo}</span>
                                {!aberto && <Contagem valor={somaDasContagens(sub) || null} />}
                                <ChevronDown className={cn("size-3.5 shrink-0 text-zinc-400 transition-transform", aberto && "rotate-180")} aria-hidden />
                            </button>
                            {aberto && (
                                <>
                                    {sub.antesDosItens && (
                                        <div className="mb-1 ml-2 mt-1" onPointerDownCapture={() => fixarAberto(sub)} onKeyDownCapture={() => fixarAberto(sub)}>
                                            {sub.antesDosItens}
                                        </div>
                                    )}
                                    {subArvore(sub.itens, fundura + 1, aoNavegar)}
                                </>
                            )}
                        </li>
                    );
                }
                const aceso = sub.chave === folhaActiva;
                const folha = (
                    <li key={sub.chave}>
                        <LinkMenu
                            item={sub}
                            onNavegar={aoNavegar}
                            aria-current={aceso ? "page" : undefined}
                            className={cn(
                                "relative flex min-h-9 items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors md:min-h-0",
                                aceso
                                    ? "bg-portal-suave font-semibold text-portal-texto dark:bg-white/5 dark:text-white"
                                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white",
                            )}
                        >
                            {aceso && fundura === 1 && <span className="absolute -left-[13px] top-1.5 h-[calc(100%-12px)] w-0.5 rounded-full bg-portal dark:bg-portal-claro" aria-hidden />}
                            <span className="flex-1 truncate">{sub.rotulo}</span>
                            <Contagem valor={sub.contagem} aceso={aceso} />
                        </LinkMenu>
                    </li>
                );
                // Para que serve a entrada (uma linha): a dica à direita, ao passar o rato ou ao focar.
                return sub.descricao ? (
                    <Dica key={sub.chave} rotulo={sub.descricao} lado="right">
                        {folha}
                    </Dica>
                ) : (
                    folha
                );
            })}
        </ul>
    );

    // -----------------------------------------------------------------------
    // Recolhido: só os ícones
    // -----------------------------------------------------------------------
    if (recolhido) {
        return (
            <nav ref={navRef} aria-label={nome} className="scroll-bonito min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-2">
                {itens.map((item) => {
                    const noCaminho = chavesActivas.has(item.chave);
                    const n = somaDasContagens(item);
                    const icone = (
                        <>
                            <Icon icon={item.icone || "mdi:circle-small"} fontSize={22} className={!noCaminho && item.cor ? CORES_DO_ICONE[item.cor] : undefined} />
                            {n > 0 && (
                                <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white dark:ring-zinc-900">
                                    {n > 99 ? "99+" : n}
                                </span>
                            )}
                        </>
                    );
                    const classe = cn(
                        "relative mx-auto flex size-11 items-center justify-center rounded-xl transition-colors",
                        noCaminho ? "bg-portal-suave text-portal dark:bg-white/10 dark:text-portal-claro" : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100",
                    );

                    if (item.itens?.length) {
                        return <GrupoFlutuante key={item.chave} item={item} classe={classe} icone={icone} conteudo={(fechar) => subArvore(item.itens!, 1, fechar)} />;
                    }
                    return (
                        <Dica key={item.chave} rotulo={n ? `${item.rotulo} · ${n}` : item.rotulo} lado="right">
                            <LinkMenu item={item} aria-label={item.rotulo} aria-current={item.chave === folhaActiva ? "page" : undefined} className={classe}>
                                {icone}
                            </LinkMenu>
                        </Dica>
                    );
                })}
            </nav>
        );
    }

    // -----------------------------------------------------------------------
    // Largo: a árvore
    // -----------------------------------------------------------------------
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {pesquisavel && (
                <div className="shrink-0 px-3 pb-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
                        <input
                            type="search"
                            value={termo}
                            onChange={(e) => setTermo(e.currentTarget.value)}
                            onKeyDown={(e) => e.key === "Escape" && setTermo("")}
                            placeholder="Procurar no menu"
                            aria-label="Procurar no menu"
                            className="h-10 w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-8 pr-8 text-base text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-portal focus:bg-white focus:ring-2 focus:ring-portal/20 md:h-9 md:text-sm dark:border-zinc-800 dark:bg-zinc-800/60 dark:text-zinc-100 dark:focus:bg-zinc-900 [&::-webkit-search-cancel-button]:hidden"
                        />
                        {termo && (
                            <button type="button" aria-label="Limpar" onClick={() => setTermo("")} className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-200/70 hover:text-zinc-700 dark:hover:bg-white/10">
                                <X className="size-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            <nav ref={navRef} aria-label={nome} className="scroll-bonito min-h-0 flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden px-3 pb-4">
                {visiveis.length === 0 && <p className="px-3 py-6 text-center text-sm text-zinc-500">Nada no menu com «{termo}».</p>}
                {visiveis.map((item) => {
                    const noCaminho = chavesActivas.has(item.chave);
                    const corIcone = cn(
                        "shrink-0",
                        noCaminho
                            ? "text-portal dark:text-portal-claro"
                            : (item.cor && CORES_DO_ICONE[item.cor]) || "text-zinc-400 group-hover:text-portal dark:text-zinc-500 dark:group-hover:text-portal-claro",
                    );

                    if (item.itens?.length) {
                        const aberto = grupoAberto(item);
                        return (
                            <div key={item.chave}>
                                <button
                                    type="button"
                                    aria-expanded={aberto}
                                    onClick={() => alternarGrupo(item, aberto)}
                                    className={cn(
                                        "group flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors md:min-h-0",
                                        noCaminho ? "font-semibold text-zinc-900 dark:text-white" : "font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/5 dark:hover:text-white",
                                    )}
                                >
                                    <Icon icon={item.icone || "mdi:folder-outline"} fontSize={20} className={corIcone} />
                                    <span className="flex-1 truncate">{item.rotulo}</span>
                                    {!aberto && <Contagem valor={somaDasContagens(item) || null} />}
                                    <ChevronDown className={cn("size-4 shrink-0 text-zinc-400 transition-transform duration-200", aberto && "rotate-180")} aria-hidden />
                                </button>
                                {aberto && (
                                    <>
                                        {item.antesDosItens && (
                                            <div className="ml-[33px] mt-1 pr-1" onPointerDownCapture={() => fixarAberto(item)} onKeyDownCapture={() => fixarAberto(item)}>
                                                {item.antesDosItens}
                                            </div>
                                        )}
                                        {subArvore(item.itens, 1, onNavegar)}
                                    </>
                                )}
                            </div>
                        );
                    }

                    const aceso = item.chave === folhaActiva;
                    return (
                        <LinkMenu
                            key={item.chave}
                            item={item}
                            onNavegar={onNavegar}
                            aria-current={aceso ? "page" : undefined}
                            className={cn(
                                "group flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors md:min-h-0",
                                aceso
                                    ? "bg-portal-suave font-semibold text-portal-texto dark:bg-white/5 dark:text-white"
                                    : "font-medium text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/5 dark:hover:text-white",
                            )}
                        >
                            <Icon icon={item.icone || "mdi:circle-small"} fontSize={20} className={corIcone} />
                            <span className="flex-1 truncate">{item.rotulo}</span>
                            <Contagem valor={item.contagem} aceso={aceso} />
                        </LinkMenu>
                    );
                })}
            </nav>
        </div>
    );
}

/** Um grupo no menu recolhido: o ícone abre os sub-itens à direita (ao passar ou ao tocar). */
function GrupoFlutuante({ item, classe, icone, conteudo }: { item: ItemPainel; classe: string; icone: React.ReactNode; conteudo: (fechar: () => void) => React.ReactNode }) {
    const [aberto, setAberto] = useState(false);
    const temporizador = useRef<number>();

    const abrirDepois = () => {
        window.clearTimeout(temporizador.current);
        temporizador.current = window.setTimeout(() => setAberto(true), 90);
    };
    const fecharDepois = () => {
        window.clearTimeout(temporizador.current);
        temporizador.current = window.setTimeout(() => setAberto(false), 180);
    };
    useEffect(() => () => window.clearTimeout(temporizador.current), []);

    return (
        <Popover.Root open={aberto} onOpenChange={setAberto}>
            <Popover.Trigger asChild>
                <button type="button" aria-label={item.rotulo} className={classe} onPointerEnter={(e) => e.pointerType === "mouse" && abrirDepois()} onPointerLeave={(e) => e.pointerType === "mouse" && fecharDepois()}>
                    {icone}
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="right"
                    align="start"
                    sideOffset={18}
                    collisionPadding={12}
                    onPointerEnter={() => window.clearTimeout(temporizador.current)}
                    onPointerLeave={fecharDepois}
                    className="scroll-bonito z-[300] max-h-[min(70vh,560px)] w-64 overflow-y-auto rounded-xl border border-zinc-200 bg-white p-2 shadow-xl shadow-zinc-950/10 animate-in fade-in-0 zoom-in-95 dark:border-zinc-800 dark:bg-zinc-900"
                >
                    <p className="flex items-center gap-2 px-2.5 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                        {item.rotulo}
                    </p>
                    {item.antesDosItens && <div className="px-1 pb-2">{item.antesDosItens}</div>}
                    <div className="-ml-[21px]">{conteudo(() => setAberto(false))}</div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
